import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiPlus, FiSearch, FiEdit, FiTrash2 } from "react-icons/fi";
import { FaFilter, FaUpload } from "react-icons/fa";
import {
  stockService,
  Stock,
  StockListResponse,
} from "../services/stockService";
import MainHeader from "../components/MainHeader";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  Crypto,
  CryptoListResponse,
  cryptoService,
  CryptoUniverseListResponse,
} from "../services/cryptoService";
interface CryptoManagementProps {
  onBackToDashboard: () => void;
}

const CryptoManagement: React.FC<CryptoManagementProps> = ({
  onBackToDashboard,
}) => {
  const [cryptos, setCrypto] = useState<Crypto[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCrypto, setTotalCrypto] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [exchangeFilter, setExchangeFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCrypto, setEditingCrypto] = useState<Crypto | null>(null);
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cryptoToDelete, setCryptoToDelete] = useState<Crypto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);

  const fetchCrypto = async () => {
    setLoading(true);
    try {
      const response: CryptoListResponse = await cryptoService.getCryptos(
        currentPage,
        10,
        searchTerm,
        exchangeFilter,
        sectorFilter,
        activeFilter === null ? undefined : activeFilter
      );
      console.log("responseresponseresponseresponseresponse", response);
      setCrypto(response?.coins);
      setTotalPages(response.total_pages);
      setTotalCrypto(response.total);
    } catch (error) {
      console.error("Error fetching crypto:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCrypto();
  }, [currentPage, searchTerm, exchangeFilter, sectorFilter, activeFilter]);

  const handleCreateStock = async (cryptoData: Partial<Stock>) => {
    try {
      await cryptoService.createCrypto(cryptoData);
      setShowCreateModal(false);
      fetchCrypto();
    } catch (error) {
      console.error("Error creating crypto:", error);
    }
  };

  const handleUpdateCrypto = async (
    cryptoId: string,
    cryptoData: Partial<Stock>
  ) => {
    setLoadingItems((prev) => new Set(prev).add(cryptoId));
    try {
      await cryptoService.updateCrypto(cryptoId, cryptoData);
      fetchCrypto();
    } catch (error) {
      console.error("Error updating stock:", error);
    } finally {
      setLoadingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cryptoId);
        return newSet;
      });
    }
  };

  const handleDeleteCrypto = (crypto: Crypto) => {
    setCryptoToDelete(crypto);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!cryptoToDelete) return;

    try {
      setDeleteLoading(true);
      await cryptoService.deleteCrypto(cryptoToDelete.id);
      setShowDeleteModal(false);
      setCryptoToDelete(null);
      fetchCrypto();
    } catch (error) {
      console.error("Error deleting crypto:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCryptoToDelete(null);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setExchangeFilter("");
    setSectorFilter("");
    setActiveFilter(null);
  };

  return (
    <div>
      <MainHeader onBackToDashboard={onBackToDashboard || (() => {})} />
      {/* page header */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <motion.button
                onClick={onBackToDashboard}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Dashboard
              </motion.button>

              <motion.h1
                className="text-3xl font-bold text-gray-900"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Crypto Management
              </motion.h1>

              <motion.p
                className="mt-2 text-sm text-gray-600"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Manage crypto-currencies, listings, and assets
              </motion.p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FiPlus />
                Add Crypto
              </button>
            </div>
          </div>
          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search crypto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FaFilter />
                Filters
              </button>

              {(searchTerm ||
                exchangeFilter ||
                sectorFilter ||
                activeFilter !== null) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Clear All
                </button>
              )}
            </div>

            {showFilters && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={exchangeFilter}
                  onChange={(e) => setExchangeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Exchanges</option>
                  <option value="NSE">NSE</option>
                  <option value="BSE">BSE</option>
                </select>

                <input
                  type="text"
                  placeholder="Sector filter..."
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />

                <select
                  value={activeFilter === null ? "" : activeFilter.toString()}
                  onChange={(e) =>
                    setActiveFilter(
                      e.target.value === "" ? null : e.target.value === "true"
                    )
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            )}
          </div>

          {/* Crypto Table*/}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading crypto...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          image
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Symbol
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Crypto Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Symbol ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cryptos?.map((crypto) => (
                        <tr key={crypto.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-medium text-gray-900">
                              <img
                                src={crypto.image}
                                alt={crypto.image}
                                width={30}
                                height={30}
                              />
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {crypto.symbol}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {crypto.name || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {crypto.symbol_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  crypto.is_active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {crypto.is_active ? "Active" : "Inactive"}
                              </span>
                              <button
                                onClick={() =>
                                  handleUpdateCrypto(crypto.id, {
                                    is_active: !crypto.is_active,
                                  })
                                }
                                disabled={loadingItems.has(crypto.id)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
                                  crypto.is_active
                                    ? "bg-blue-600"
                                    : "bg-gray-200"
                                }`}
                              >
                                {loadingItems.has(crypto.id) ? (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                  </div>
                                ) : (
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                      crypto.is_active
                                        ? "translate-x-5"
                                        : "translate-x-1"
                                    }`}
                                  />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingCrypto(crypto);
                                  setShowEditModal(true);
                                }}
                                disabled={loadingItems.has(crypto.id)}
                                className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                              >
                                {loadingItems.has(crypto.id) ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border border-blue-600 border-t-transparent"></div>
                                ) : (
                                  <FiEdit />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteCrypto(crypto)}
                                disabled={loadingItems.has(crypto.id)}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                {loadingItems.has(crypto.id) ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border border-red-600 border-t-transparent"></div>
                                ) : (
                                  <FiTrash2 />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * 10 + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(currentPage * 10, totalCrypto)}
                        </span>{" "}
                        of <span className="font-medium">{totalCrypto}</span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            const page =
                              Math.max(
                                1,
                                Math.min(totalPages - 4, currentPage - 2)
                              ) + i;
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  page === currentPage
                                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          }
                        )}
                        <button
                          onClick={() =>
                            setCurrentPage(
                              Math.min(totalPages, currentPage + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Create Stock Modal */}
      {showCreateModal && (
        <CreateStockModal
          onClose={() => {
            setShowCreateModal(false);
            setSubmitLoader(false);
          }}
          onSubmit={handleCreateStock}
          submitLoader={submitLoader}
          setSubmitLoader={setSubmitLoader}
        />
      )}

      {/* Edit Crypto Modal */}
      {showEditModal && editingCrypto && (
        <EditCryptoModal
          crypto={editingCrypto}
          onClose={() => {
            setShowEditModal(false);
            setEditingCrypto(null);
            setSubmitLoader(false);
          }}
          onSubmit={handleUpdateCrypto}
          submitLoader={submitLoader}
          setSubmitLoader={setSubmitLoader}
        />
      )}

      {/* Delete Crypto Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Crypto"
        message={`Are you sure you want to delete the crypto "${cryptoToDelete?.symbol}" (${cryptoToDelete?.name})?`}
        confirmText="Delete"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        loading={deleteLoading}
      />
    </div>
  );
};

// Create Stock Modal Component
interface CreateStockModalProps {
  onClose: () => void;
  onSubmit: (cryptoData: Partial<Stock>) => void;
  submitLoader: boolean;
  setSubmitLoader: (loading: boolean) => void;
}

const CreateStockModal: React.FC<CreateStockModalProps> = ({
  onClose,
  onSubmit,
  submitLoader,
  setSubmitLoader,
}) => {
  const [formData, setFormData] = useState({
    symbol: "",
    name: "",
    image: "",
    symbol_id: "",
    is_active: true,
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // For now, we'll use a placeholder URL
      // In a real implementation, you'd upload to a cloud service
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          image: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoader(true);
    try {
      await onSubmit({
        ...formData,
      });
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Create New Crypto
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Banner Image *
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {formData.image ? (
                    <div className="mb-4">
                      <img
                        src={formData.image}
                        alt="Banner preview"
                        className="mx-auto h-32 w-auto object-contain rounded"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, image: "" }))
                        }
                        className="mt-2 text-sm text-red-600 hover:text-red-500"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <>
                      <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="image-upload"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                        >
                          <FaUpload className="w-4 h-4 mr-2" />
                          Upload Image
                        </label>
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="sr-only"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Symbol *
              </label>
              <input
                type="text"
                required
                value={formData.symbol}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    symbol: e.target.value.toUpperCase(),
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Symbol Id
              </label>
              <input
                type="text"
                value={formData.symbol_id}
                onChange={(e) =>
                  setFormData({ ...formData, symbol_id: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitLoader}
              >
                {submitLoader ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Crypto Modal Component
interface EditCryptoModalProps {
  crypto: Crypto;
  onClose: () => void;
  onSubmit: (cryptoId: string, cryptoData: Partial<Crypto>) => void;
  submitLoader: boolean;
  setSubmitLoader: (loading: boolean) => void;
}

const EditCryptoModal: React.FC<EditCryptoModalProps> = ({
  crypto,
  onClose,
  onSubmit,
  submitLoader,
  setSubmitLoader,
}) => {
  const [formData, setFormData] = useState({
    symbol: crypto.symbol,
    name: crypto.name || "",
    image: crypto.image || "",
    symbol_id: crypto.symbol_id || "",
    is_active: crypto.is_active,
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          image: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoader(true);
    try {
      await onSubmit(crypto.id, {
        ...formData,
      });
      onClose();
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Edit Crypto
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Crypto Image *
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  {formData.image ? (
                    <div className="mb-4">
                      <img
                        src={formData.image}
                        alt="Crypto preview"
                        className="mx-auto h-32 w-auto object-contain rounded"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, image: "" }))
                        }
                        className="mt-2 text-sm text-red-600 hover:text-red-500"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <>
                      <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="image-upload-edit"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                        >
                          <FaUpload className="w-4 h-4 mr-2" />
                          Upload Image
                        </label>
                        <input
                          id="image-upload-edit"
                          name="image-upload-edit"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="sr-only"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Symbol *
              </label>
              <input
                type="text"
                required
                value={formData.symbol}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    symbol: e.target.value.toUpperCase(),
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Symbol ID
              </label>
              <input
                type="text"
                value={formData.symbol_id}
                onChange={(e) =>
                  setFormData({ ...formData, symbol_id: e.target.value })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitLoader}
              >
                {submitLoader ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Bulk Upload Modal Component

export default CryptoManagement;
